
import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { useIsMobile } from '@/hooks/use-mobile';
import LiveSessionHeader from '@/components/collaboration/LiveSessionHeader';
import LiveSessionContent from '@/components/collaboration/LiveSessionContent';
import SessionNotFound from '@/components/collaboration/session/SessionNotFound';
import MediaPermissions from '@/components/collaboration/session/MediaPermissions';
import { mockCollaborationSessions, mockCollaborationProjects } from '@/types/collaboration';
import { useToast } from '@/hooks/use-toast';
import { useAudioRecorder } from '@/hooks/useAudioRecorder';

const LiveSession = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { toast } = useToast();
  
  // Find session and project data directly
  const session = mockCollaborationSessions.find(s => s.id === sessionId);
  const project = session ? mockCollaborationProjects.find(p => p.id === session.projectId) : null;
  
  // Audio recorder hook
  const {
    isRecording,
    recordings,
    elapsedTime,
    audioLevel,
    startRecording,
    stopRecording,
    downloadRecording,
    clearRecordings,
    deleteRecording // Add the new function
  } = useAudioRecorder();
  
  // State management
  const [isMicActive, setIsMicActive] = React.useState(true);
  const [isVideoActive, setIsVideoActive] = React.useState(true);
  const [isAudioActive, setIsAudioActive] = React.useState(true);
  const [isScreenSharing, setIsScreenSharing] = React.useState(false);
  const [showSidebar, setShowSidebar] = React.useState(!isMobile);
  const [screenStream, setScreenStream] = React.useState<MediaStream | null>(null);
  const [systemAudioStream, setSystemAudioStream] = React.useState<MediaStream | null>(null);
  
  useEffect(() => {
    if (isMobile) {
      setShowSidebar(false);
    } else {
      setShowSidebar(true);
    }
  }, [isMobile]);
  
  useEffect(() => {
    console.log("Current route: /collaboration/sessions/" + sessionId);
    console.log("Is video active:", isVideoActive);
    
    if (!session) return;
    
    document.title = `${session.name} | Live Session`;
    
    return () => {
      document.title = "TuneConnective";
    };
  }, [session, sessionId, isVideoActive]);
  
  if (!session) {
    return (
      <Layout>
        <SessionNotFound sessionId={sessionId} />
      </Layout>
    );
  }
  
  const handleToggleSidebar = () => {
    setShowSidebar(!showSidebar);
  };
  
  const handleLeaveSession = () => {
    if (isRecording) {
      stopRecording();
    }
    
    if (screenStream) {
      screenStream.getTracks().forEach(track => track.stop());
      setScreenStream(null);
      setIsScreenSharing(false);
    }
    
    toast({
      title: "Session left",
      description: "You have left the collaboration session."
    });
  };
  
  const toggleRecording = () => {
    if (isRecording) {
      stopRecording();
      toast({
        title: "Recording stopped",
        description: "Your session recording has been saved. Go to the Recordings tab to export it."
      });
    } else {
      startRecording();
      toast({
        title: "Recording started",
        description: "Your session is now being recorded. Audio will be available when you stop recording."
      });
    }
  };
  
  const handleShareScreen = async () => {
    if (isScreenSharing) {
      if (screenStream) {
        screenStream.getTracks().forEach(track => track.stop());
        setScreenStream(null);
        setIsScreenSharing(false);
        
        toast({
          title: "Screen sharing stopped",
          description: "You've stopped sharing your screen."
        });
      }
    } else {
      try {
        // Check if running on mobile
        if (isMobile) {
          toast({
            title: "Mobile Screen Sharing",
            description: "Screen sharing may be limited on mobile devices. Please use desktop for full functionality.",
            variant: "default"
          });
        }
        
        // Request screen sharing using the browser's API
        const stream = await navigator.mediaDevices.getDisplayMedia({ 
          video: true,
          audio: true 
        });
        
        // When user stops sharing via browser UI, detect it and update state
        stream.getVideoTracks()[0].onended = () => {
          if (screenStream) {
            screenStream.getTracks().forEach(track => track.stop());
          }
          setScreenStream(null);
          setIsScreenSharing(false);
          
          toast({
            title: "Screen sharing stopped",
            description: "You've stopped sharing your screen."
          });
        };
        
        setScreenStream(stream);
        setIsScreenSharing(true);
        
        toast({
          title: "Screen sharing started",
          description: "You are now sharing your screen."
        });
      } catch (error) {
        console.error("Error sharing screen:", error);
        
        // Check if error is related to NotAllowedError which happens on iOS Safari
        if (error instanceof DOMException && error.name === "NotAllowedError") {
          toast({
            title: "Screen sharing not allowed",
            description: "Your browser or device doesn't support screen sharing or permission was denied.",
            variant: "destructive"
          });
        } else {
          toast({
            title: "Screen sharing failed",
            description: "Unable to share your screen. Please try again or use a different device.",
            variant: "destructive"
          });
        }
        setIsScreenSharing(false);
      }
    }
  };
  
  return (
    <Layout>
      <div className="flex flex-col h-full overflow-hidden bg-gradient-to-b from-[#151520] to-[#1A1F2C]">
        <MediaPermissions />
        
        <LiveSessionHeader 
          sessionName={session.name}
          projectName={project ? project.name : 'Unknown Project'}
          isRecording={isRecording}
          elapsedTime={elapsedTime}
          sessionDuration={session.duration}
          onLeave={() => {
            handleLeaveSession();
            navigate(`/collaboration/projects/${session.projectId}`);
          }}
          onToggleRecording={toggleRecording}
          onToggleSidebar={handleToggleSidebar}
        />
        
        <div className="flex-1 overflow-hidden">
          <LiveSessionContent
            isMobile={isMobile}
            showSidebar={showSidebar}
            setShowSidebar={setShowSidebar}
            isMicActive={isMicActive}
            isVideoActive={isVideoActive}
            isAudioActive={isAudioActive}
            isScreenSharing={isScreenSharing}
            screenStream={screenStream}
            onToggleMic={() => setIsMicActive(!isMicActive)}
            onToggleVideo={() => setIsVideoActive(!isVideoActive)}
            onToggleAudio={() => setIsAudioActive(!isAudioActive)}
            onShareScreen={handleShareScreen}
            onLeaveSession={() => {
              handleLeaveSession();
              navigate(`/collaboration/projects/${session.projectId}`);
            }}
            isRecording={isRecording}
            onToggleRecording={toggleRecording}
            recordings={recordings}
            onDownloadRecording={downloadRecording}
            onClearRecordings={clearRecordings}
            onDeleteRecording={deleteRecording}
            session={session}
            elapsedTime={elapsedTime}
            audioLevel={audioLevel}
            systemAudioStream={systemAudioStream}
            onSystemAudioStreamChange={setSystemAudioStream}
            sessionId={sessionId || ''}
            userId="mock-user-id"
            displayName="You"
          />
        </div>
      </div>
    </Layout>
  );
};

export default LiveSession;
